/* GTJ auto load-more (replaces the Scroll Bee app)
   =========================================================================
   Why this exists: Scroll Bee appended pages into the grid but kept NO record
   of what it had loaded. Measured 31Aug in Playwright WebKit AND Chromium at
   390x664: forward = 48 products / docHeight 8137 / scrollY 7473; tap a
   product, press back = 24 products / docHeight 4579 / scrollY 3849. The
   browser tries to restore 7473px into a page that is only 4579px tall, so it
   clamps to the bottom of page 1 - which reads as "it threw me back to the
   top". Identical in both engines, so it was never a WebKit issue.

   This file does the same job but remembers state, so back-navigation lands
   exactly where the shopper left off.

   Design notes:
   - ES5 only (no arrow functions / let / const / template literals), matching
     assets/gtj-speed.js, so old Android WebViews and Safari < 11 still run it.
   - Dawn's real pagination stays in the DOM (crawlable, and the no-JS
     fallback); it is only hidden visually once we take over, and is put BACK
     if a fetch ever fails.
   - Nothing listens for `unload`, so the browser back-forward cache is not
     disqualified. When bfcache does kick in the whole DOM is restored for free
     and this script deliberately does nothing (see the pageshow guard).
   - Appended cards are server-rendered by card-product.liquid, so they already
     carry .gtj-badge / .gtj-bookmark / .gtj-plus. We only strip Dawn's
     scroll-trigger animation classes (an appended card that is never observed
     stays at opacity 0 forever) and re-run the theme own init hooks.
   ========================================================================= */

(function () {
  'use strict';

  /* v8: on-screen debug readout, dormant unless the URL contains
     "gtjlm-debug" (e.g. /collections/all?gtjlm-debug=1). Round-7w playbook:
     when a device-only failure cannot be reproduced in automation, make the
     DEVICE report which branch ran instead of guessing. Purely additive. */
  var BUILD = 'lm-v14';
  var DBG = false;
  try { DBG = /gtjlm-debug/.test(location.href) || sessionStorage.getItem('gtjlm-dbg') === '1'; } catch (e) {}
  var dbgT0 = 0, dbgEl = null, dbgLines = [];
  try { dbgT0 = (window.performance && performance.now) ? 0 : 0; } catch (e) {}
  function dlog(msg) {
    if (!DBG) return;
    try {
      sessionStorage.setItem('gtjlm-dbg', '1');   /* survive the product hop */
      dbgLines.push(msg);
      if (dbgLines.length > 14) dbgLines.shift();
      if (!dbgEl) {
        dbgEl = document.createElement('div');
        dbgEl.style.cssText = 'position:fixed;left:4px;right:4px;bottom:4px;z-index:2147483647;' +
          'background:rgba(0,0,0,.85);color:#0f0;font:10px/1.35 monospace;padding:6px;border-radius:6px;' +
          'pointer-events:none;white-space:pre-wrap;word-break:break-all;';
        (document.body || document.documentElement).appendChild(dbgEl);
      }
      dbgEl.textContent = BUILD + '\n' + dbgLines.join('\n');
    } catch (e) {}
  }

  /* v11 - USER VETO of the v9 trade + the correct signal at last. He is
     right: tapping 'View All' into a collection must start at the TOP; only a
     RETURN from a product should restore. Navigation types lie on iOS (v9)
     and referrers lie on back-reloads (v5) - but this script runs on EVERY
     page, so it can keep its own one-step breadcrumb of the previous page in
     sessionStorage. prev page was a product/cart => return => restore. prev
     was anything else (home, menu link, another collection, a refresh of the
     same listing) => fresh entry => top, state cleared. The cancelled-
     navigation yank (v10) involves no page load, so the breadcrumb stays
     untouched and that guard keeps working. */
  var prevPath = '';
  try {
    prevPath = sessionStorage.getItem('gtjlm:last') || '';
    sessionStorage.setItem('gtjlm:last', location.pathname);
  } catch (e) {}
  function isReturnFromProduct() {
    return prevPath.indexOf('/products/') === 0 || prevPath.indexOf('/cart') === 0;
  }

  var GRID_SEL = '#product-grid';
  var PAGER_SEL = '.pagination-wrapper';
  var KEY_PREFIX = 'gtjlm:';
  var ROOT_MARGIN = '900px 0px';   /* start fetching ~1.5 screens early */
  var MAX_RESTORE_PAGES = 20;      /* cap the work a single back-press can cause */
  var RESTORE_CONCURRENCY = 4;     /* parallel page fetches while restoring */
  var REASSERT_MS = 9000;          /* stop re-placing the shopper after this */
  var STATE_TTL_MS = 30 * 60 * 1000; /* forget a position older than 30 minutes */

  /* TRAP (cost an hour, 31Aug): Dawn base.css ships
        a:empty, ul:empty, dl:empty, div:empty, section:empty, ... { display: none }
     so an empty <div> sentinel is display:none, is never laid out, and an
     IntersectionObserver on it NEVER fires. We therefore observe the LAST
     product <li> instead and re-target it after every append - it always has
     content, so it can never be hidden by that rule. */
  var grid = null, pager = null, watched = null, statusEl = null, io = null;
  var currentPage = 1, maxPage = 1;
  var loading = false, finished = false, restoring = false, started = false;
  var lastClickedHref = '';        /* product opened from the grid */
  var lastClickedTop = 0;          /* that card's document-top when it was tapped */
  var userScrolled = false;        /* shopper took over - stop re-placing them */
  var lastInputT = 0;              /* last real user input (touch/wheel/key) */
  /* v10 - OBSERVED via the user's lm-v9 box + timing report: tap a product
     and press back IMMEDIATELY -> iOS CANCELS the provisional navigation, the
     collection page never reloads (no new boot in the box) - and Safari then
     re-applies the STALE scroll offset it had memorised for the history
     entry, yanking the still-alive page to the top. Waiting a second lets the
     navigation commit, so the return goes through boot()+restore and works.
     Since the page stays alive, we can catch the yank: after a product click,
     arm a short watch; if the page jumps to ~top within a few seconds WITHOUT
     any user input, put the shopper back where they were. */
  var pendingBack = null;          /* {y, until} armed by a product click */
  var parked = false;              /* auto-load paused until the shopper scrolls */

  function qs(s, r) { return (r || document).querySelector(s); }
  function qsa(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }

  /* BUG I SHIPPED AND THEN FOUND (31Aug audit): the two listing templates do
     NOT share markup. On a COLLECTION, #product-grid IS the <ul>. On SEARCH
     RESULTS, #product-grid is a DIV wrapper (.template-search__results) and
     the <ul class="product-grid"> lives inside it. A hard-coded
     '#product-grid > li.grid__item' therefore matched 0 items on search - yet
     the code still hid the pagination, so /search?q=ring was capped at page 1
     with pages 2-11 unreachable. Resolve the real list instead, and never take
     over a page where we cannot find items. */
  function resolveGrid(root) {
    var host = (root || document).querySelector(GRID_SEL);
    if (!host) return null;
    if (host.tagName === 'UL' && itemsIn(host).length) return host;
    var inner = host.querySelector('ul.product-grid') || host.querySelector('ul');
    return (inner && itemsIn(inner).length) ? inner : (itemsIn(host).length ? host : null);
  }

  function itemsIn(node) {
    if (!node) return [];
    var out = [], kids = node.children;
    for (var i = 0; i < kids.length; i++) if (kids[i].tagName === 'LI') out.push(kids[i]);
    return out;
  }

  function pageOf(href) {
    var m = /[?&]page=(\d+)/.exec(href || '');
    return m ? parseInt(m[1], 10) : 0;
  }

  /* Params that must NOT take part in identity. THE BUG THIS FIXES (31Aug,
     user: "if the product page is still loading and I press back it goes to
     the top"): a recommendation app rewrites the collection URL with
     `_su_rec` + `_su_rec_id`, and the id carries a TIMESTAMP
     (..._su_rec_id=4ced6f7f-...-1788204897). Because the state key contained
     location.search verbatim, the key we saved under and the key we looked up
     on the way back were different strings whenever that token had changed or
     had not been applied yet - so no state was found and the shopper landed at
     the top. Whether it matched was pure timing, which is why it looked
     random and why a fully-loaded product page (token already settled) worked.
     Anything ad/analytics/session flavoured is stripped; real filters
     (filter.*, sort_by, q) are kept because they DO change which products
     belong on the page. */
  /* v14 (user's box: key=/collections/bestsellers?pb=0, saved=NONE,
     returnFromProduct=true - the breadcrumb was RIGHT and the key was split):
     Shopify's PREVIEW BAR sometimes appends ?pb=0 to navigations inside a
     theme preview, and whether it does is timing luck - that was the "now
     its random". preview_theme_id and our own gtjlm-debug param split keys
     the same way. All three are infrastructure, not identity. */
  function isVolatileParam(name) {
    return name.charAt(0) === '_' ||
      /^(utm_|fbclid|gclid|gbraid|wbraid|msclkid|srsltid|ttclid|twclid|igshid|epik|irclickid|mc_eid|mc_cid|ref|variant|page|pb|preview_theme_id|gtjlm-debug)$|^utm_/i.test(name);
  }

  /* stable, identity-only query string: volatile params dropped, rest sorted
     so a different param ORDER cannot produce a different key */
  function stableSearch() {
    var q = location.search.replace(/^\?/, '');
    if (!q) return '';
    var parts = q.split('&');
    var keep = [];
    for (var i = 0; i < parts.length; i++) {
      if (!parts[i]) continue;
      var name = parts[i].split('=')[0];
      if (isVolatileParam(name)) continue;
      keep.push(parts[i]);
    }
    keep.sort();
    return keep.length ? '?' + keep.join('&') : '';
  }

  /* current URL minus page + tracking noise - real filters are preserved */
  function baseUrl() {
    return location.pathname + stableSearch();
  }

  function urlForPage(n) {
    var u = baseUrl();
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'page=' + n;
  }

  /* identity of THIS listing, independent of tracking tokens and page number */
  function stateKey() { return KEY_PREFIX + location.pathname + stableSearch(); }

  function saveState() {
    try {
      if (currentPage <= 1) { sessionStorage.removeItem(stateKey()); return; }
      sessionStorage.setItem(stateKey(), JSON.stringify({
        p: currentPage,
        y: Math.round(window.pageYOffset || document.documentElement.scrollTop || 0),
        /* document height, so the restore can make the page tall enough to
           hold the old scroll position BEFORE any content has arrived */
        h: Math.round(document.documentElement.scrollHeight || 0),
        /* the product the shopper opened - anchoring to this element is far
           more reliable than a pixel offset if any row renders a hair taller */
        a: lastClickedHref || '',
        /* where that card sat in the document when tapped. Restoring
           (anchorTopNow - (at - y)) reproduces the EXACT framing the shopper
           had, and self-corrects for any row above it rendering taller. */
        at: lastClickedTop || 0,
        t: (new Date()).getTime()      /* age, so stale state cannot surprise */
      }));
    } catch (e) { /* private mode / quota - degrade to stock behaviour */ }
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(stateKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function clearState() {
    try { sessionStorage.removeItem(stateKey()); } catch (e) {}
  }

  function isBackForward() {
    try {
      var e = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (e && e.type) return e.type === 'back_forward';
      if (performance.navigation) return performance.navigation.type === 2;
    } catch (err) {}
    return false;
  }

  /* "Remember where we came from."
     Do NOT trust the navigation type alone. On a real iPhone the return from a
     product is sometimes reported as a plain `navigate` (page evicted, product
     URL redirected e.g. to add ?variant=, or Safari simply did a fresh load),
     and the old code then treated it as a brand-new visit, WIPED the saved
     position and dropped the shopper at the top. That is the remaining
     "sometimes perfect, sometimes top" case: it depended on which of the two
     paths Safari happened to take.

     The referrer is the honest signal - if we arrived here from a product page
     on this same shop, this is a return, whatever the navigation type says. */
  function cameFromProduct() {
    try {
      var r = document.referrer || '';
      if (!r) return false;
      if (r.indexOf(location.protocol + '//' + location.host) !== 0) return false;
      return r.indexOf('/products/') > -1;
    } catch (e) { return false; }
  }

  function isReturnVisit() { return isBackForward() || cameFromProduct(); }

  /* ---- UI bits ------------------------------------------------------- */

  function makeStatus() {
    var el = document.createElement('div');
    el.className = 'gtj-lm-status';
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('role', 'status');
    el.style.cssText = 'text-align:center;padding:2.4rem 0 1rem;font-size:1.1rem;' +
      'letter-spacing:.08em;text-transform:uppercase;color:#8C8C8C;min-height:1.4rem;';
    return el;
  }

  function setStatus(text) { if (statusEl) statusEl.textContent = text || ''; }

  function hidePager(hide) {
    if (!pager) return;
    pager.style.display = hide ? 'none' : '';
  }

  /* ---- fetching / appending ------------------------------------------ */

  function fetchItems(n) {
    return fetch(urlForPage(n), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return itemsIn(resolveGrid(doc));
      });
  }

  function appendItems(items) {
    if (!items || !items.length) return 0;
    var frag = document.createDocumentFragment();
    var added = [];
    for (var i = 0; i < items.length; i++) {
      var el = document.importNode(items[i], true);
      /* Dawn hides .scroll-trigger items until its observer sees them; an
         appended node is never observed, so it would stay invisible. */
      el.className = String(el.className)
        .replace(/\bscroll-trigger--offscreen\b/g, '')
        .replace(/\bscroll-trigger\b/g, '')
        .replace(/\s+/g, ' ');
      frag.appendChild(el);
      added.push(el);
    }
    grid.appendChild(frag);
    reinit(added);
    armWatcher();
    return added.length;
  }

  /* observe the last card in the grid; re-target after every append */
  function armWatcher() {
    if (!io || !grid || parked) return;
    var last = grid.lastElementChild;
    if (!last || last === watched) return;
    if (watched) { try { io.unobserve(watched); } catch (e) {} }
    watched = last;
    io.observe(watched);
  }

  /* Re-run the theme own initialisers over the new cards. All guarded:
     these are gtj-card-extras.js internals and may be renamed. */
  function reinit(nodes) {
    var fns = ['gtjMarkSaved', 'gtjInitMultiQuicks', 'gtjWatchRecent'];
    for (var i = 0; i < fns.length; i++) {
      try { if (typeof window[fns[i]] === 'function') window[fns[i]](); } catch (e) {}
    }
    try {
      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger();
      }
    } catch (e) {}
    /* belt and braces: anything still invisible gets forced visible */
    setTimeout(function () {
      for (var j = 0; j < nodes.length; j++) {
        try {
          if (nodes[j].isConnected && getComputedStyle(nodes[j]).opacity === '0') {
            nodes[j].style.opacity = '1';
            nodes[j].style.transform = 'none';
          }
        } catch (e) {}
      }
    }, 300);
  }

  function loadNext() {
    if (loading || finished || restoring) return;
    if (currentPage >= maxPage) { finish(); return; }
    loading = true;
    var want = currentPage + 1;
    setStatus('Loading more');
    fetchItems(want).then(function (items) {
      var n = appendItems(items);
      loading = false;
      if (!n) { finish(); return; }
      currentPage = want;
      saveState();
      setStatus('');
      if (currentPage >= maxPage) finish();
    })['catch'](function () {
      /* network failed - give the shopper the real pagination back */
      loading = false;
      finished = true;
      setStatus('');
      hidePager(false);
      if (io && watched) { try { io.unobserve(watched); } catch (e) {} }
    });
  }

  function finish() {
    finished = true;
    setStatus('');
    if (io && watched) { try { io.unobserve(watched); } catch (e) {} }
    watched = null;
  }

  /* ---- restore after a back-press ------------------------------------ */

  /* Put the shopper back where they were.

     The naive version (fetch page 2, append, fetch page 3, append, ... then
     scroll) is CORRECT but feels broken: measured on this store with 9 pages
     loaded, the shopper sat at scrollY 0 for ~4s before being moved, which
     reads as "it threw me to the top" - exactly the reported symptom, and
     exactly why it only showed up with lots of products loaded.

     So: (1) prop the document open to its old height immediately and place
     the shopper on the FIRST frame, (2) fetch the missing pages in parallel
     rather than one after another, (3) keep re-asserting the position as rows
     land, until the anchor card exists or the shopper takes over. */
  function restore(target, y, savedH, anchorHref, anchorTop) {
    restoring = true;
    userScrolled = false;
    hidePager(true);

    var stop = Math.min(target, currentPage + MAX_RESTORE_PAGES, maxPage);
    var deadline = Date.now() + REASSERT_MS;

    /* (1) instant placement - hold the old height open so the browser will
       accept the old offset before a single row has arrived */
    if (savedH > 0) document.body.style.minHeight = savedH + 'px';
    try { window.scrollTo(0, y); } catch (e) {}
    dlog('placed y=' + Math.round(window.pageYOffset || 0) + ' (want ' + y + ') minH=' + savedH);

    function place() {
      if (userScrolled || Date.now() > deadline) return;
      var target = y;
      var anchor = (anchorHref && anchorTop > 0 && grid)
        ? grid.querySelector('a[href^="' + anchorHref.split('?')[0] + '"]')
        : null;
      if (anchor) {
        /* keep the tapped card at the same spot on screen it occupied before,
           NOT centred - centring moves the view by up to half a screen */
        var nowTop = anchor.getBoundingClientRect().top + (window.pageYOffset || 0);
        target = Math.round(nowTop - (anchorTop - y));
      }
      /* SANITY CLAMP. Measured 31Aug on a referrer-return: the anchor maths
         once produced a target ~28,000px BELOW the saved position and the page
         walked itself to the bottom, pulling in pages nobody asked for. The
         saved offset is the source of truth; the anchor may only refine it by
         up to one screen. */
      var limit = window.innerHeight || 700;
      if (Math.abs(target - y) > limit) target = y;
      window.scrollTo(0, Math.max(0, target));
    }

    /* (2) parallel fetches, appended strictly in page order */
    var pages = [];
    for (var n = currentPage + 1; n <= stop; n++) pages.push(n);
    if (!pages.length) { restoring = false; finishRestore(); return Promise.resolve(); }

    var results = {}, nextToAppend = pages[0], inFlight = 0, idx = 0, failed = false;

    function drainInOrder() {
      while (results[nextToAppend]) {
        appendItems(results[nextToAppend]);
        currentPage = nextToAppend;
        delete results[nextToAppend];
        nextToAppend++;
        place();                        /* (3) re-assert as rows land */
      }
    }

    return new Promise(function (resolve) {
      function pump() {
        if (failed) return;
        if (idx >= pages.length && inFlight === 0) { resolve(); return; }
        while (inFlight < RESTORE_CONCURRENCY && idx < pages.length) {
          (function (page) {
            inFlight++;
            fetchItems(page).then(function (items) {
              results[page] = items;
              inFlight--;
              drainInOrder();
              pump();
            })['catch'](function () {
              inFlight--;
              failed = true;
              resolve();
            });
          })(pages[idx++]);
        }
      }
      pump();
    }).then(function () {
      restoring = false;
      finishRestore();
    });

    function finishRestore() {
      dlog('restore done p=' + currentPage + ' y=' + Math.round(window.pageYOffset || 0));
      /* v13: a late Safari yank can also land AFTER a reload-restore
         completes - put the same persistent guard on watch for a few seconds */
      var ry = Math.round(window.pageYOffset || 0);
      if (ry > 400) pendingBack = { y: ry, until: (new Date()).getTime() + 3000, tapT: (new Date()).getTime() };
      setStatus('');
      document.body.style.minHeight = '';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          place();
          saveState();
          /* PARK auto-loading until the shopper actually scrolls. After a
             restore they are sitting deep in the list, so the watched last
             card is permanently inside the observer margin and every append
             instantly triggers the next one - measured runaway from 7 pages
             to 15 in a few seconds. Re-arm on the first real scroll. */
          parked = true;
          if (io && watched) { try { io.unobserve(watched); } catch (e) {} }
          if (currentPage >= maxPage) finish();
        });
      });
    }
  }

  /* ---- wiring -------------------------------------------------------- */

  function teardown() {
    if (io) { try { io.disconnect(); } catch (e) {} io = null; }
    if (statusEl && statusEl.parentNode) statusEl.parentNode.removeChild(statusEl);
    watched = null; statusEl = null;
  }

  /* Stand down if the Scroll Bee app is still active, otherwise BOTH would
     append and every product would appear twice. This makes the switch-over a
     no-op push: nothing changes until the Scroll Bee app embed is turned off
     in the theme editor, at which point this takes over on the next load. */
  function appOwnsIt() {
    return !!(window.ScrollBee || window.ScrollBeeLastHandledHREF);
  }

  function start() {
    if (appOwnsIt()) return false;
    grid = resolveGrid(document);
    pager = qs(PAGER_SEL);
    if (!grid || !pager) return false;            /* not a listing page */
    /* fail safe: if we cannot see any items we must NOT hide the pagination,
       or the shopper loses every page after the first */
    if (!itemsIn(grid).length) return false;
    if (!window.fetch || !window.IntersectionObserver || !window.DOMParser) return false;

    var links = qsa('a.pagination__item', pager);
    maxPage = 1;
    for (var i = 0; i < links.length; i++) {
      var p = pageOf(links[i].getAttribute('href'));
      if (p > maxPage) maxPage = p;
    }
    currentPage = pageOf(location.search) || 1;
    if (maxPage <= 1 || currentPage >= maxPage) return false;   /* nothing to add */

    finished = false; loading = false; restoring = false;

    statusEl = makeStatus();
    grid.parentNode.insertBefore(statusEl, grid.nextSibling);

    hidePager(true);

    io = new IntersectionObserver(function (entries) {
      for (var k = 0; k < entries.length; k++) {
        if (entries[k].isIntersecting) { loadNext(); break; }
      }
    }, { rootMargin: ROOT_MARGIN });
    armWatcher();

    started = true;
    return true;
  }

  function boot() {
    var saved = readState();
    dlog('boot nav=' + (function(){try{var e=performance.getEntriesByType('navigation')[0];return e?e.type:'?';}catch(x){return 'err';}})() + ' bf=' + isBackForward());
    dlog('key=' + stateKey().slice(6, 60));
    dlog('saved=' + (saved ? JSON.stringify(saved).slice(0, 70) : 'NONE'));
    var fresh = !!(saved && saved.t && ((new Date()).getTime() - saved.t) < STATE_TTL_MS);

    /* v9 - OBSERVED ON THE USER'S IPHONE (lm-v8 debug box, 1Sep): a real
       back-press from a product arrived as nav=navigate, bf=false, AND the
       referrer was NOT the product page (a back-reload keeps the page's
       ORIGINAL referrer - the v5 cameFromProduct heuristic rested on a wrong
       assumption and never fires on this path). Every navigation-type-based
       return detector is therefore unreliable on iOS. NEW RULE: the state
       itself is the signal - if a fresh (< TTL) position exists for THIS
       listing, restore it, whatever the browser calls the navigation. A
       genuinely new browse simply overwrites state as the shopper scrolls,
       and expired state is dropped. */
    dlog('prev=' + (prevPath || '(none)') + ' returnFromProduct=' + isReturnFromProduct());
    if (saved && !fresh) { dlog('state EXPIRED - clearing'); clearState(); saved = null; }
    if (saved && !isReturnFromProduct()) { dlog('FRESH ENTRY (prev not product/cart) - clearing, starting at top'); clearState(); saved = null; }

    if (!start()) { dlog('start()=false (no grid/pager/items or app owns)'); return; }
    dlog('start ok maxPage=' + maxPage + ' curPage=' + currentPage + ' items=' + itemsIn(grid).length);

    if (saved && fresh && saved.p > currentPage) {
      /* stop the browser clamping us to the bottom of page 1 before the
         extra rows exist - we place the shopper ourselves once they do */
      dlog('RESTORING to p=' + saved.p + ' y=' + saved.y);
      try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (e) {}
      restore(saved.p, saved.y, saved.h || 0, saved.a || '', saved.at || 0);
    } else {
      dlog('no restore: saved=' + !!saved + ' fresh=' + fresh + ' savedP=' + (saved ? saved.p : '-') + ' cur=' + currentPage);
    }
  }

  /* save on the way out. pagehide covers both a normal navigation and a
     bfcache freeze, and unlike unload it does not disable bfcache. */
  window.addEventListener('pagehide', saveState);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') saveState();
  });
  /* capture-phase so drawer/card handlers that stopPropagation cannot hide
     the click from us (CLAUDE.md gotcha 17) */
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!t || !started) return;
    var href = t.getAttribute('href') || '';
    /* remember WHICH product was opened, so the restore can anchor to that
       exact card instead of trusting a pixel offset */
    if (href.indexOf('/products/') > -1 && grid && grid.contains(t)) {
      lastClickedHref = href;
      lastClickedTop = Math.round(t.getBoundingClientRect().top + (window.pageYOffset || 0));
    }
    saveState();
    var cy = Math.round(window.pageYOffset || 0);
    if (href.indexOf('/products/') > -1) {
      /* v12 - USER REPRO ('immediate back -> top' even after v11): a back
         press can land AFTER the product page COMMITS but BEFORE its defer
         scripts run - the product page dies without writing the breadcrumb,
         the trail still names this collection, and v11 wrongly calls the
         return a fresh entry. Never depend on the dying page: stamp the
         breadcrumb with the DESTINATION here, while this page is alive. If
         the product loads fully its own script writes the same value; if the
         navigation is cancelled entirely (v10 case), the guard cleanup below
         rewrites it back to this collection. */
      try { sessionStorage.setItem('gtjlm:last', href.split('?')[0]); } catch (e) {}
      if (cy > 400) {
        pendingBack = { y: cy, until: (new Date()).getTime() + 4000, tapT: (new Date()).getTime() };
        dlog('yank-guard armed y=' + cy);
      }
    }
    dlog('click-save p=' + currentPage + ' y=' + cy + ' -> ' + href.slice(0, 34));
  }, true);

  /* if the shopper starts scrolling during a restore, stop moving them */
  function markUserScroll() {
    userScrolled = true;
    lastInputT = (new Date()).getTime();
    if (parked) { parked = false; armWatcher(); }   /* re-arm auto-load */
  }

  /* undo Safari's cancelled-navigation scroll yank (see pendingBack above) */
  window.addEventListener('scroll', function () {
    if (!pendingBack) return;
    var now = (new Date()).getTime();
    if (now > pendingBack.until) {
      pendingBack = null;
      try { sessionStorage.setItem('gtjlm:last', location.pathname); } catch (e) {}
      return;
    }
    var y = window.pageYOffset || 0;
    /* input from BEFORE/AT the arming tap does not count - only fresh input
       after it means the user is scrolling deliberately */
    var freshInput = lastInputT > pendingBack.tapT + 250 && (now - lastInputT) <= 400;
    if (freshInput) {
      /* the shopper took over - stand down for good */
      pendingBack = null;
      try { sessionStorage.setItem('gtjlm:last', location.pathname); } catch (e) {}
      return;
    }
    if (y < 100) {
      /* v13 (user: fastest back STILL -> top): STAY ARMED. Safari re-applies
         its stale offset over several frames on a cancelled navigation, so a
         one-shot undo wins the first round and loses the war. Re-place on
         every yank until the window closes or the user genuinely scrolls. */
      pendingBack.fired = (pendingBack.fired || 0) + 1;
      if (pendingBack.fired <= 3) dlog('yank undone #' + pendingBack.fired + ' -> y=' + pendingBack.y);
      window.scrollTo(0, pendingBack.y);
      saveState();
    }
  }, { passive: true });
  window.addEventListener('pagehide', function () { pendingBack = null; });
  window.addEventListener('wheel', markUserScroll, { passive: true });
  window.addEventListener('touchstart', markUserScroll, { passive: true });
  window.addEventListener('keydown', function (e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageDown' || e.key === 'PageUp') markUserScroll();
  });

  window.addEventListener('pageshow', function (e) {
    /* restored from bfcache: the DOM and scroll are already correct - do
       nothing at all, or we would append duplicates */
    if (e.persisted) return;
  });

  /* Dawn facet filtering swaps #product-grid out entirely; re-arm when the
     grid node is replaced, otherwise auto-load silently dies after a filter. */
  function watchForGridSwap() {
    var host = document.getElementById('ProductGridContainer') || document.body;
    if (!host || !window.MutationObserver) return;
    var mo = new MutationObserver(function () {
      var live = resolveGrid(document);
      if (live && live !== grid) {
        teardown();
        currentPage = pageOf(location.search) || 1;
        clearState();
        start();
      }
    });
    mo.observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); watchForGridSwap(); });
  } else {
    boot(); watchForGridSwap();
  }
})();
